// frontend/src/theme.js
import { createTheme } from '@mui/material/styles';

import { blue, orange, green, red, purple, grey } from '@mui/material/colors';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2061A1', // Strong blue - represents trust, stability
      light: '#4A85BD',
      dark: '#0A4076',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FF8C29', // Vibrant orange - construction/visibility/action
      light: '#FFB067',
      dark: '#D06100',
      contrastText: '#000000',
    },
    error: { main: '#D32F2F', light: '#EF5350', dark: '#C62828' },
    warning: { main: '#FFC107', light: '#FFD54F', dark: '#FFA000' },
    info: { main: '#0288D1', light: '#4FC3F7', dark: '#01579B' },
    success: { main: '#388E3C', light: '#66BB6A', dark: '#1B5E20' },
    text: {
      primary: '#1C2A3A', 
      secondary: '#546E7A', 
      disabled: '#90A4AE',
    },
    background: {
      default: '#FFFFFF', 
      paper: '#FFFFFF', 
      alternate: '#ECF0F5',
      tableContainer: '#FFFFFF', // New: ensure table container matches table
    },
    divider: '#CFD8DC',
    // Action button colors for different types of interactions
    action: {
      primary: '#FF8C29', // Primary action buttons (Log New Bid) - using orange
      secondary: '#2061A1', // Secondary action buttons (View Feedback) - using blue
      disabled: '#ECEFF1', // Background for disabled buttons
      disabledText: '#90A4AE', // Text for disabled buttons
    },
    // These names must match the `colorScheme` strings in statusUtils.js
    blue: {
      main: blue[700],
      contrastText: '#fff',
    },
    orange: {
      main: orange[800],
      contrastText: '#fff',
    },
    green: {
      main: green[700],
      contrastText: '#fff',
    },
    red: {
      main: red[700],
      contrastText: '#fff',
    },
    purple: {
      main: purple[500],
      contrastText: '#fff',
    },
    gray: { // Renamed from 'grey' to match 'colorScheme: gray'
      main: grey[600],
      contrastText: '#fff',
    },
    custom: {
      bidHighlight: '#E3F2FD',
      bidPositive: '#E8F5E9',
      bidNeutral: '#FFF8E1',
      bidNegative: '#FFEBEE',
      chartColors: ['#2061A1', '#FF8C29', '#388E3C', '#7E57C2', '#26A69A', '#F57C00'],
      // Container styling
      cardShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      tableBorder: '#ECF0F5',
      tableHeaderBg: '#F5F7FA', // Light background for table headers (optional)
      tableRowHover: '#F9FAFC', // Very slight highlight on hover
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 600 }, 
    h2: { fontWeight: 600 }, 
    h3: { fontWeight: 500 },
    button: { fontWeight: 500, textTransform: 'none' },
    // Add styles for the stat cards
    stats: {
      label: {
        fontSize: '0.75rem',
        fontWeight: 500,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: '#546E7A',
      },
      value: {
        fontWeight: 600,
      },
      valueSmall: {
        fontSize: '1.75rem',
      },
      valueLarge: {
        fontSize: '2.25rem',
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none', 
    '0px 2px 4px rgba(0, 0, 0, 0.05)', 
    '0px 4px 6px rgba(0, 0, 0, 0.07)',
    '0px 5px 7px rgba(0, 0, 0, 0.08)', 
    '0px 6px 8px rgba(0, 0, 0, 0.09)',
    '0px 7px 9px rgba(0, 0, 0, 0.1)',
    // Add shadows up to index 24 if needed, or remove the shadows override
    // if you only want to customize the first few.
    'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
  ],
  components: {
    // Override styles for specific MUI components
    MuiPaper: {
      styleOverrides: {
        root: {
          transition: 'none !important',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // For the primary "Log New Bid" button
        containedPrimary: {
          backgroundColor: '#FF8C29',
          '&:hover': {
            backgroundColor: '#D06100',
          },
        },
        // For the "View Feedback" buttons
        containedSecondary: {
          backgroundColor: '#2061A1',
          '&:hover': {
            backgroundColor: '#0A4076',
          },
        },
      },
    },
   
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            color: '#546E7A',
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F9FAFC',
          },
          // Add a border bottom to each row
          '& td': {
            borderBottom: '1px solid #ECF0F5',
          },
        },
      },
    },
  },
});

export default theme; // Export the theme object